import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ProgressBar } from 'react-bootstrap';
import Select from 'react-select';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import styledComponent from '../../utils/styledComponent';
import { CreatePalletMutation } from '../../libs/GraphQL';
import track from '../../utils/analytics';
import {
  buildPalletInput,
  isAlreadyExistsError,
  normalizePalletQr,
} from './palletUtils';
import useFetchPallets from './useFetchPallets';
import PalletQrScanner, { FEEDBACK_DISPLAY_MS } from './PalletQrScanner';

const SectionHeader = styledComponent('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  '& h4': {
    margin: 0,
    fontWeight: 500,
  },
  '& p': {
    margin: '5px 0 0 0',
    color: '#818387',
    fontSize: '14px',
  },
});

const FilterGroup = styledComponent('div', {
  display: 'inline-block',
  minWidth: '200px',
  '& label': {
    color: '#000',
    fontSize: '18px',
    fontWeight: 800,
    marginBottom: '10px',
    display: 'block',
  },
  '& .SingleDatePickerInput__withBorder': {
    border: 'none',
  },
  '& .SingleDatePicker': {
    display: 'block',
  },
  '& .SingleDatePicker input': {
    background: '#FFF',
    border: '1px solid #CCC',
    borderRadius: '4px',
    fontSize: '16px',
    height: '50px',
    padding: '15px',
    width: '200px',
  },
});

const selectStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: '4px',
    border: '1px solid #CCC',
    minHeight: '50px',
    minWidth: '200px',
  }),
};

const SetupCard = styledComponent('div', {
  maxWidth: '500px',
  margin: '50px auto',
  padding: '40px',
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: '4px',
  '& h5': {
    textAlign: 'center',
    marginBottom: '30px',
    fontWeight: 500,
  },
  '& .form-group': {
    marginBottom: '20px',
  },
});

const ScanArea = styledComponent('div', {
  textAlign: 'center',
  padding: '40px',
  background: '#f6f8f5',
  border: '2px dashed #ccc',
  borderRadius: '8px',
  marginBottom: '20px',
});

const ScanInput = styledComponent('input', {
  fontSize: '18px',
  padding: '15px 20px',
  width: '100%',
  maxWidth: '400px',
  textAlign: 'center',
  height: 'auto',
  background: '#FFF',
  border: '1px solid #CCC',
  borderRadius: '4px',
});

const ScanInputHelpText = styledComponent('p', {
  color: '#818387',
  marginBottom: '15px',
});

const ProgressSection = styledComponent('div', {
  background: '#e8f4fd',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '20px',
});

const SummaryCard = styledComponent('div', {
  background: '#f6f8f5',
  border: '1px solid #ddd',
  borderRadius: '4px',
  padding: '20px',
  marginBottom: '20px',
});

const SummaryTitle = styledComponent('div', {
  fontWeight: 700,
  fontSize: '16px',
  marginBottom: '15px',
  color: '#2c2d31',
});

const Timer = styledComponent('div', {
  fontSize: '24px',
  fontWeight: 700,
  color: '#d9283f',
});

const WarningBanner = styledComponent('div', {
  background: '#fff3cd',
  border: '1px solid #ff9e22',
  color: '#856404',
  padding: '12px 16px',
  borderRadius: '4px',
  marginBottom: '20px',
  fontSize: '14px',
});

const ScanList = styledComponent('ul', {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  '& li': {
    padding: '10px 0',
    borderBottom: '1px solid #eee',
    fontSize: '16px',
  },
});

const StartButton = styledComponent('button', {
  backgroundColor: '#368007',
  border: 'none',
  borderRadius: '4px',
  color: '#fff',
  fontSize: '14px',
  padding: '15px',
  width: '100%',
  marginTop: '30px',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#008a00',
  },
  ':disabled': {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
});

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Values must be valid backend PalletVarianceType enum values.
const VARIANCE_REASON_OPTIONS = [
  { value: 'MISSING_AT_LOAD', label: 'Missing at load' },
  { value: 'WRONG_LOCATION', label: 'Wrong location' },
];

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const LoadScanningTab = ({ selectedDate, onDateChange }) => {
  const [isDateFocused, setIsDateFocused] = useState(false);
  const { pallets, isLoading, error, fetchPallets } =
    useFetchPallets(selectedDate);
  const [userEmail, setUserEmail] = useState('');

  // Setup state
  const [linehaulFilter, setLinehaulFilter] = useState(null);
  const [middleMileFilter, setMiddleMileFilter] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Scanning state
  const [scannedPallets, setScannedPallets] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [scanError, setScanError] = useState(null);

  // Modal state
  const [showVarianceModal, setShowVarianceModal] = useState(false);
  const [showWrongPalletModal, setShowWrongPalletModal] = useState(false);
  const [wrongPalletInfo, setWrongPalletInfo] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [varianceReason, setVarianceReason] = useState('MISSING_AT_LOAD');
  const [varianceNotes, setVarianceNotes] = useState('');
  const [varianceError, setVarianceError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timerRef = useRef(null);
  const timeoutRef = useRef(null);
  const scanInputRef = useRef(null);

  // Fetch current user email
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const session = await Auth.currentAuthenticatedUser();
        setUserEmail(session.attributes.email);
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    fetchUser();
  }, []);

  // Extract linehaul options
  const linehaulOptions = useMemo(() => {
    const linehauls = new Set();
    pallets.forEach((p) => {
      if (p.linehaulFirstMile) linehauls.add(p.linehaulFirstMile);
    });
    return Array.from(linehauls).map((v) => ({ value: v, label: v }));
  }, [pallets]);

  // Extract middle mile options based on selected linehaul
  const middleMileOptions = useMemo(() => {
    const middleMiles = new Set();
    pallets
      .filter(
        (p) => !linehaulFilter || p.linehaulFirstMile === linehaulFilter.value,
      )
      .forEach((p) => {
        if (p.linehaulMiddleMile) middleMiles.add(p.linehaulMiddleMile);
      });
    return Array.from(middleMiles).map((v) => ({ value: v, label: v }));
  }, [pallets, linehaulFilter]);

  // Pallets for selected linehaul (and optionally middle mile)
  const linehaulPallets = useMemo(() => {
    if (!linehaulFilter) return [];
    return pallets.filter((p) => {
      if (p.linehaulFirstMile !== linehaulFilter.value) return false;
      if (middleMileFilter && p.linehaulMiddleMile !== middleMileFilter.value)
        return false;
      return true;
    });
  }, [pallets, linehaulFilter, middleMileFilter]);

  // Remaining (unscanned) pallets
  const remainingPallets = useMemo(() => {
    const scannedIds = new Set(scannedPallets.map((s) => s.uniqueId));
    return linehaulPallets.filter(
      (p) =>
        !scannedIds.has(p.uniqueId) &&
        p.loadStatus !== 'LOADED' &&
        p.loadStatus !== 'RECEIVED',
    );
  }, [linehaulPallets, scannedPallets]);

  // Market breakdown for summary
  const marketBreakdown = useMemo(() => {
    const breakdown = {};
    linehaulPallets.forEach((p) => {
      const market = p.market || 'Unknown';
      breakdown[market] = (breakdown[market] || 0) + 1;
    });
    return breakdown;
  }, [linehaulPallets]);

  const progressPercent =
    linehaulPallets.length > 0
      ? Math.round((scannedPallets.length / linehaulPallets.length) * 100)
      : 0;

  // Timer
  useEffect(() => {
    if (isScanning) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        handleCompleteWithVariance();
      }, SESSION_TIMEOUT_MS);

      return () => {
        clearInterval(timerRef.current);
        clearTimeout(timeoutRef.current);
      };
    }
    return undefined;
  }, [isScanning]);

  useEffect(() => {
    if (
      isScanning &&
      scanInputRef.current &&
      !window.matchMedia('(pointer: coarse)').matches
    ) {
      scanInputRef.current.focus();
    }
  }, [isScanning]);

  // Reset middle mile when linehaul changes
  useEffect(() => {
    setMiddleMileFilter(null);
  }, [linehaulFilter]);

  const startScanning = () => {
    // Pre-populate with already loaded/received pallets
    const alreadyLoaded = linehaulPallets
      .filter((p) => p.loadStatus === 'LOADED' || p.loadStatus === 'RECEIVED')
      .map((p) => ({
        ...p,
        scannedAt: 'previously',
      }));
    setScannedPallets(alreadyLoaded);
    setElapsedSeconds(0);
    setScanError(null);
    setIsScanning(true);
  };

  const cancelScanning = () => {
    track('Load Scanning Session Cancelled', {
      linehaul: linehaulFilter?.value,
      middleMile: middleMileFilter?.value || null,
      palletsScanned: scannedPallets.length,
      palletsExpected: linehaulPallets.length,
      elapsedSeconds,
    });
    clearInterval(timerRef.current);
    clearTimeout(timeoutRef.current);
    setIsScanning(false);
    setScannedPallets([]);
    setElapsedSeconds(0);
    setScanError(null);
  };

  const handleScan = useCallback(
    async (qrCode) => {
      setScanError(null);

      // Find the pallet matching this QR code. Labels are printed with the
      // PENDING status baked in, so compare status-agnostically.
      const normalizedCode = normalizePalletQr(qrCode);
      const matchedPallet = pallets.find(
        (p) =>
          normalizePalletQr(p.qrCode) === normalizedCode ||
          normalizePalletQr(p.uniqueId) === normalizedCode,
      );

      if (!matchedPallet) {
        setScanError('Pallet not found. Please enter a valid pallet QR code.');
        return { status: 'error', message: 'Pallet not found' };
      }

      // Check if already scanned
      if (scannedPallets.find((s) => s.uniqueId === matchedPallet.uniqueId)) {
        toast.info('This pallet has already been scanned.', {
          toastId: matchedPallet.uniqueId,
          autoClose: FEEDBACK_DISPLAY_MS,
        });
        return { status: 'info', message: 'Pallet already scanned' };
      }

      // Check if pallet belongs to the selected linehaul
      if (matchedPallet.linehaulFirstMile !== linehaulFilter.value) {
        setWrongPalletInfo({
          pallet: matchedPallet,
          expectedLinehaul: linehaulFilter.value,
        });
        setShowWrongPalletModal(true);
        return { status: 'error', message: 'Wrong linehaul for this pallet' };
      }

      // Create pallet record with LOADED status
      try {
        if (matchedPallet.loadStatus !== 'LOADED') {
          await CreatePalletMutation(
            buildPalletInput(matchedPallet, {
              loadStatus: 'LOADED',
              createdBy: userEmail,
            }),
          );
        }
      } catch (err) {
        if (!isAlreadyExistsError(err)) {
          console.error('Error updating pallet status:', err);
          setScanError('Failed to update pallet status. Please try again.');
          return { status: 'error', message: 'Failed to update pallet status' };
        }
      }

      {
        const scannedEntry = {
          ...matchedPallet,
          scannedAt: new Date().toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
        };

        const newScanned = [...scannedPallets, scannedEntry];
        setScannedPallets(newScanned);
        toast.success(
          `Pallet scanned: ${newScanned.length} of ${linehaulPallets.length}`,
          { toastId: matchedPallet.uniqueId, autoClose: FEEDBACK_DISPLAY_MS },
        );

        // Check if all pallets scanned
        if (newScanned.length === linehaulPallets.length) {
          clearInterval(timerRef.current);
          clearTimeout(timeoutRef.current);
          setShowSuccessModal(true);
        }

        return {
          status: 'success',
          message: `Pallet scanned: ${newScanned.length} of ${linehaulPallets.length}`,
        };
      }
    },
    [pallets, scannedPallets, linehaulFilter, linehaulPallets, userEmail],
  );

  const handleScanSubmit = (e) => {
    e.preventDefault();
    if (scanInput.trim()) {
      handleScan(scanInput.trim());
      setScanInput('');
    }
  };

  const handleCompleteWithVariance = () => {
    setVarianceError(null);
    setShowVarianceModal(true);
  };

  const selectedVarianceReason =
    VARIANCE_REASON_OPTIONS.find((o) => o.value === varianceReason) ||
    VARIANCE_REASON_OPTIONS[0];

  const handleConfirmVariance = async () => {
    setIsSubmitting(true);
    setVarianceError(null);
    try {
      const scannedIds = new Set(scannedPallets.map((s) => s.uniqueId));
      const missingPallets = linehaulPallets.filter(
        (p) => !scannedIds.has(p.uniqueId),
      );

      await Promise.all(
        missingPallets.map((p) =>
          CreatePalletMutation(
            buildPalletInput(p, {
              loadStatus: 'VARIANCE',
              varianceType: varianceReason,
              varianceNotes,
              createdBy: userEmail,
            }),
          ).catch((err) => {
            if (!isAlreadyExistsError(err)) throw err;
          }),
        ),
      );

      clearInterval(timerRef.current);
      clearTimeout(timeoutRef.current);
      setShowVarianceModal(false);
      setIsScanning(false);
      setScannedPallets([]);
      setElapsedSeconds(0);
      setVarianceReason('MISSING_AT_LOAD');
      setVarianceNotes('');
      fetchPallets();
    } catch (err) {
      console.error('Error recording variance:', err);
      setVarianceError(
        err?.errors?.[0]?.message ||
          err?.message ||
          'Failed to record variance. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwitchLinehaul = () => {
    if (wrongPalletInfo) {
      setLinehaulFilter({
        value: wrongPalletInfo.pallet.linehaulFirstMile,
        label: wrongPalletInfo.pallet.linehaulFirstMile,
      });
      setShowWrongPalletModal(false);
      setWrongPalletInfo(null);
      cancelScanning();
    }
  };

  const handleSuccessDone = () => {
    setShowSuccessModal(false);
    setIsScanning(false);
    setScannedPallets([]);
    setElapsedSeconds(0);
    fetchPallets();
  };

  const missingPallets = useMemo(() => {
    const scannedIds = new Set(scannedPallets.map((s) => s.uniqueId));
    return linehaulPallets.filter((p) => !scannedIds.has(p.uniqueId));
  }, [linehaulPallets, scannedPallets]);

  // Setup view
  if (!isScanning) {
    return (
      <div style={{ paddingTop: '20px' }}>
        <SectionHeader>
          <div>
            <h4>Load Scanning</h4>
            <p>Scan pallets at truck loading</p>
          </div>
        </SectionHeader>

        <SetupCard>
          <h5>Select production date and linehaul to begin scanning</h5>

          <div className="form-group">
            <FilterGroup>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="load-production-date">Production Date</label>
              <SingleDatePicker
                date={selectedDate}
                isOutsideRange={() => false}
                onDateChange={onDateChange}
                focused={isDateFocused}
                onFocusChange={({ focused }) => setIsDateFocused(focused)}
                id="load-production-date"
                numberOfMonths={1}
              />
            </FilterGroup>
          </div>

          <div className="form-group">
            <FilterGroup>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="load-linehaul">Linehaul</label>
              <Select
                inputId="load-linehaul"
                options={linehaulOptions}
                value={linehaulFilter}
                onChange={setLinehaulFilter}
                isClearable
                placeholder="Select Linehaul..."
                styles={selectStyles}
                isLoading={isLoading}
              />
            </FilterGroup>
          </div>

          <div className="form-group">
            <FilterGroup>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="load-middle-mile">Middle Mile (optional)</label>
              <Select
                inputId="load-middle-mile"
                options={middleMileOptions}
                value={middleMileFilter}
                onChange={setMiddleMileFilter}
                isClearable
                placeholder="All"
                styles={selectStyles}
                isDisabled={!linehaulFilter}
              />
            </FilterGroup>
          </div>

          {error && (
            <WarningBanner>
              <strong>Error:</strong> {error}
            </WarningBanner>
          )}

          <StartButton
            onClick={startScanning}
            disabled={
              !selectedDate || !linehaulFilter || linehaulPallets.length === 0
            }
          >
            Start Scanning
          </StartButton>

          {linehaulFilter && !isLoading && linehaulPallets.length === 0 && (
            <p
              style={{
                textAlign: 'center',
                color: '#818387',
                marginTop: '15px',
              }}
            >
              No pallets found for this linehaul and date.
            </p>
          )}
        </SetupCard>
      </div>
    );
  }

  // Active scanning view
  return (
    <div style={{ paddingTop: '20px' }}>
      <SectionHeader>
        <div>
          <h4>Load Scanning - {linehaulFilter?.value}</h4>
          <p>Scan pallets at truck loading</p>
        </div>
        <Timer>
          <span className="glyphicon glyphicon-time" />{' '}
          {formatTime(elapsedSeconds)} elapsed
        </Timer>
      </SectionHeader>

      <WarningBanner>
        <strong>Session Timeout:</strong> Complete scanning within 30 minutes or
        current scan count will be recorded as final (with variance if
        incomplete).
      </WarningBanner>

      <SummaryCard>
        <SummaryTitle>LINEHAUL SUMMARY - {linehaulFilter?.value}</SummaryTitle>
        <div className="row">
          <div className="col-sm-4">
            <strong>Total Pallets Expected:</strong> {linehaulPallets.length}
          </div>
          <div className="col-sm-8">
            {Object.entries(marketBreakdown).map(([market, count]) => (
              <span key={market} style={{ marginRight: '20px' }}>
                {market} &rarr; {count} {count === 1 ? 'pallet' : 'pallets'}
              </span>
            ))}
          </div>
        </div>
      </SummaryCard>

      <form onSubmit={handleScanSubmit}>
        <ScanArea>
          <h5>Scan Pallet QR Code</h5>
          <PalletQrScanner
            onScan={handleScan}
            disabled={
              showWrongPalletModal || showVarianceModal || showSuccessModal
            }
          />
          <ScanInputHelpText>
            Or type the pallet QR code below and press Enter.
          </ScanInputHelpText>
          <ScanInput
            ref={scanInputRef}
            type="text"
            placeholder="Enter QR code..."
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
          />
        </ScanArea>
      </form>

      {scanError && (
        <WarningBanner>
          <strong>Error:</strong> {scanError}
        </WarningBanner>
      )}

      <ProgressSection>
        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
          <strong style={{ fontSize: '18px' }}>
            {scannedPallets.length} of {linehaulPallets.length} pallets scanned
          </strong>
        </div>
        <ProgressBar now={progressPercent} label={`${progressPercent}%`} />
      </ProgressSection>

      <div className="row">
        <div className="col-sm-6">
          <h5 style={{ color: '#368007' }}>
            <span className="glyphicon glyphicon-ok" /> SCANNED
          </h5>
          <ScanList>
            {scannedPallets.map((p) => (
              <li key={p.uniqueId} style={{ color: '#368007' }}>
                <span className="glyphicon glyphicon-ok" /> Pallet{' '}
                {p.palletSequence}/{linehaulPallets.length} - {p.market}{' '}
                <small style={{ color: '#818387' }}>({p.scannedAt})</small>
              </li>
            ))}
          </ScanList>
        </div>
        <div className="col-sm-6">
          <h5 style={{ color: '#818387' }}>
            <span className="glyphicon glyphicon-unchecked" /> REMAINING
          </h5>
          <ScanList>
            {remainingPallets.map((p) => (
              <li key={p.uniqueId} style={{ color: '#818387' }}>
                <span className="glyphicon glyphicon-unchecked" /> Pallet{' '}
                {p.palletSequence}/{linehaulPallets.length} - {p.market}
              </li>
            ))}
          </ScanList>
        </div>
      </div>

      <hr />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={cancelScanning}>Cancel Session</Button>
        <Button bsStyle="warning" onClick={handleCompleteWithVariance}>
          Complete with Variance
        </Button>
      </div>

      {/* Variance Modal */}
      <Modal
        show={showVarianceModal}
        onHide={() => setShowVarianceModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#bd4b00' }}>
            <span className="glyphicon glyphicon-warning-sign" /> Complete with
            Variance
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {varianceError && (
            <WarningBanner>
              <strong>Error:</strong> {varianceError}
            </WarningBanner>
          )}
          <p>
            You are completing with{' '}
            <strong>
              {scannedPallets.length} of {linehaulPallets.length}
            </strong>{' '}
            pallets scanned.
          </p>
          <p>
            <strong>{missingPallets.length} pallets</strong> will be marked as{' '}
            {selectedVarianceReason.label}.
          </p>

          <div style={{ marginBottom: '20px' }}>
            <strong>Missing Pallets:</strong>
            <ul style={{ marginTop: '10px' }}>
              {missingPallets.map((p) => (
                <li key={p.uniqueId}>
                  Pallet {p.palletSequence}/{linehaulPallets.length} -{' '}
                  {p.market}
                </li>
              ))}
            </ul>
          </div>

          <div className="form-group">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="load-variance-reason" style={{ fontWeight: 700 }}>
              Variance Reason
            </label>
            <select
              id="load-variance-reason"
              className="form-control"
              value={varianceReason}
              onChange={(e) => setVarianceReason(e.target.value)}
            >
              {VARIANCE_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="load-variance-notes" style={{ fontWeight: 700 }}>
              Notes (optional)
            </label>
            <textarea
              id="load-variance-notes"
              className="form-control"
              rows="3"
              style={{ height: 'auto' }}
              placeholder="Add additional details about this variance..."
              value={varianceNotes}
              onChange={(e) => setVarianceNotes(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowVarianceModal(false)}>Cancel</Button>
          <Button
            bsStyle="danger"
            onClick={handleConfirmVariance}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Recording...' : 'Confirm & Record Variance'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Wrong Pallet Modal */}
      <Modal
        show={showWrongPalletModal}
        onHide={() => setShowWrongPalletModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#d9283f' }}>
            <span className="glyphicon glyphicon-warning-sign" /> Wrong Pallet
            Scanned
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This pallet belongs to a <strong>different linehaul</strong>.
          </p>
          {wrongPalletInfo && (
            <table className="table" style={{ fontSize: '14px' }}>
              <tbody>
                <tr>
                  <td>
                    <strong>Scanned Pallet:</strong>
                  </td>
                  <td>
                    Pallet {wrongPalletInfo.pallet.palletSequence} -{' '}
                    {wrongPalletInfo.pallet.market}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Expected Linehaul:</strong>
                  </td>
                  <td>{wrongPalletInfo.expectedLinehaul}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Pallet Linehaul:</strong>
                  </td>
                  <td>{wrongPalletInfo.pallet.linehaulFirstMile}</td>
                </tr>
              </tbody>
            </table>
          )}
          <p style={{ color: '#818387' }}>
            Please scan a pallet for {linehaulFilter?.value}, or change your
            selected linehaul.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowWrongPalletModal(false);
              setWrongPalletInfo(null);
            }}
          >
            Dismiss &amp; Retry
          </Button>
          <Button bsStyle="info" onClick={handleSwitchLinehaul}>
            Switch to {wrongPalletInfo?.pallet.linehaulFirstMile} Linehaul
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={handleSuccessDone}>
        <Modal.Body style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', color: '#368007' }}>
            <span className="glyphicon glyphicon-ok-circle" />
          </div>
          <h4 style={{ color: '#368007', marginTop: '20px' }}>
            Loading Complete!
          </h4>
          <p>
            All <strong>{linehaulPallets.length} pallets</strong> successfully
            scanned and marked as LOADED.
          </p>
          <table
            className="table"
            style={{ fontSize: '14px', marginTop: '20px' }}
          >
            <tbody>
              <tr>
                <td>
                  <strong>Linehaul:</strong>
                </td>
                <td>{linehaulFilter?.value}</td>
              </tr>
              <tr>
                <td>
                  <strong>Total Pallets:</strong>
                </td>
                <td>{linehaulPallets.length}</td>
              </tr>
              <tr>
                <td>
                  <strong>Time Taken:</strong>
                </td>
                <td>{formatTime(elapsedSeconds)}</td>
              </tr>
            </tbody>
          </table>
          <Button
            style={{
              marginTop: '20px',
              padding: '12px 40px',
              backgroundColor: '#368007',
              color: '#fff',
              border: 'none',
            }}
            onClick={handleSuccessDone}
          >
            Done
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

LoadScanningTab.propTypes = {
  selectedDate: PropTypes.object,
  onDateChange: PropTypes.func.isRequired,
};

LoadScanningTab.defaultProps = {
  selectedDate: null,
};

export default LoadScanningTab;
